import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges } from '@angular/core';
import { ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { MemberAttendanceDto, CreateOrUpdateAttendanceDto } from '../../models/divine-service.model';
import { DivineServiceApiService } from '../../services/api/divine-service-api.service';
import { DivineServiceStateService } from '../../services/state/divine-service-state.service';
import { NotificationService } from '@shared/services/notification.service';
import { MemberFilterHelper } from '../../helpers';
import { DataTableComponent, PaginationConfig, TableColumn } from '@shared/components/data-table/data-table.component';
import { TableToolbarComponent, ToolbarSortSelection } from '@shared/components/table-toolbar/table-toolbar.component';
import { MemberReferenceComponent } from '@shared/components/member-reference/member-reference.component';
import { DataTableMultiSelectFilterComponent, DataTableMultiSelectFilterOption } from '@shared/components/data-table-multi-select-filter/data-table-multi-select-filter.component';

export interface AttendanceChangeEvent {
  member: MemberAttendanceDto;
  isChecked: boolean;
}

@Component({
  selector: 'app-attendance',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatCheckboxModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    EmptyStateComponent,
    MemberReferenceComponent,
    DataTableMultiSelectFilterComponent,
    DataTableComponent,
    TableToolbarComponent
  ],
  templateUrl: './attendance.component.html',
  styleUrl: './attendance.component.scss'
})
export class AttendanceComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly ROSTER_INFORMATION_KEY = 'divine-service-attendance-roster-information-seen';
  private static readonly ROSTER_INFORMATION =
    'Se muestran los miembros que pertenecían a esta comunidad en la fecha del servicio divino.';
  @ViewChild(DataTableComponent) dataTable?: DataTableComponent<MemberAttendanceDto>;
  @Input() members: MemberAttendanceDto[] = [];
  @Input() isSaving = false;
  @Input() isLoading = false;
  @Input() readonly = false;
  @Input() errorMessage = '';
  @Input() divineServiceId: string | null = null; // ID necesario para las llamadas al API

  @Output() attendanceChange = new EventEmitter<AttendanceChangeEvent>();
  @Output() markAll = new EventEmitter<string[]>();
  @Output() unmarkAll = new EventEmitter<string[]>();
  @Output() membersUpdated = new EventEmitter<MemberAttendanceDto[]>(); // Emitir cuando se actualiza
  @Output() attendanceTotalsChanged = new EventEmitter<{ adultsAttendance: number; minorsAttendance: number }>();

  searchControl = new FormControl('');
  filteredMembers: MemberAttendanceDto[] = [];
  readonly tableId = 'divine-service-attendance';
  tableDensity: 'compact' | 'normal' | 'comfortable' = 'compact';
  currentPage = 1;
  pageSize = 10;
  sortState: ToolbarSortSelection = { sortBy: 'name', direction: 'asc' };
  activeFilters: { key: string; label: string; removable: boolean }[] = [];
  readonly desktopFilterForm = new FormGroup({
    minor: new FormControl(['adult', 'minor'], { nonNullable: true }),
    sealed: new FormControl(['unsealed', 'sealed'], { nonNullable: true }),
    visitor: new FormControl(['member', 'visitor'], { nonNullable: true }),
    attended: new FormControl(['absent', 'present'], { nonNullable: true })
  });
  readonly minorFilterOptions: DataTableMultiSelectFilterOption[] = [
    { value: 'adult', label: 'Mayores' },
    { value: 'minor', label: 'Menores' },
  ];
  readonly sealedFilterOptions: DataTableMultiSelectFilterOption[] = [
    { value: 'sealed', label: 'Sellados' },
    { value: 'unsealed', label: 'No sellados' },
  ];
  readonly visitorFilterOptions: DataTableMultiSelectFilterOption[] = [
    { value: 'member', label: 'Miembros' },
    { value: 'visitor', label: 'Visitas' },
  ];
  readonly attendedFilterOptions: DataTableMultiSelectFilterOption[] = [
    { value: 'present', label: 'Presentes' },
    { value: 'absent', label: 'Ausentes' },
  ];
  readonly tableColumns: TableColumn[] = [
    { key: 'name', header: 'Miembro', sortable: true, hideable: false, minWidth: '220px' },
    { key: 'age', header: 'Edad', sortable: true, hideable: true, width: '130px' },
    { key: 'sealed', header: 'Sellado', sortable: true, hideable: true, width: '130px' },
    { key: 'visitor', header: 'Visita', sortable: true, hideable: true, visible: false, width: '120px' },
    { key: 'attended', header: 'Asistencia', sortable: true, hideable: false, width: '110px' }
  ];
  private attendanceFilters: Record<string, string[]> = {
    minor: this.minorFilterOptions.map(option => option.value),
    sealed: this.sealedFilterOptions.map(option => option.value),
    visitor: this.visitorFilterOptions.map(option => option.value),
    attended: this.attendedFilterOptions.map(option => option.value),
  };

  get desktopPageMembers(): MemberAttendanceDto[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredMembers.slice(start, start + this.pageSize);
  }

  get paginationConfig(): PaginationConfig {
    const totalPages = Math.max(1, Math.ceil(this.filteredMembers.length / this.pageSize));
    return {
      currentPage: Math.min(this.currentPage, totalPages),
      pageSize: this.pageSize,
      totalCount: this.filteredMembers.length,
      totalPages
    };
  }
  private destroy$ = new Subject<void>();
  private toggling = new Set<string>(); // Para rastrear miembros en proceso de cambio

  // Totales de asistencia
  get adultsAttendance(): number {
    return MemberFilterHelper.filterAdults(this.members, true).length;
  }

  get minorsAttendance(): number {
    return MemberFilterHelper.filterMinors(this.members, true).length;
  }

  isMinor(member: MemberAttendanceDto): boolean {
    return MemberFilterHelper.isMinor(member);
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private divineServiceService: DivineServiceApiService,
    private stateService: DivineServiceStateService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.applyFilter();
    this.desktopFilterForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(filters => {
        this.attendanceFilters = {
          minor: filters.minor ?? this.minorFilterOptions.map(option => option.value),
          sealed: filters.sealed ?? this.sealedFilterOptions.map(option => option.value),
          visitor: filters.visitor ?? this.visitorFilterOptions.map(option => option.value),
          attended: filters.attended ?? this.attendedFilterOptions.map(option => option.value),
        };
        this.refreshFilters();
      });
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.applyFilter());
    // Emitir los totales iniciales
    this.attendanceTotalsChanged.emit({
      adultsAttendance: this.adultsAttendance,
      minorsAttendance: this.minorsAttendance
    });
    this.showRosterInformationOnFirstVisit();
  }

  showRosterInformation(): void {
    this.notificationService.info(
      AttendanceComponent.ROSTER_INFORMATION,
      'Cerrar',
      { duration: 0 }
    );
  }

  ngOnChanges(): void {
    // Deshabilitar el control de búsqueda mientras se cargan los miembros
    if (this.isLoading || this.readonly) {
      this.searchControl.disable({ emitEvent: false });
    } else {
      this.searchControl.enable({ emitEvent: false });
    }
    this.applyFilter();
    // Emitir los totales cuando cambian los miembros
    this.attendanceTotalsChanged.emit({
      adultsAttendance: this.adultsAttendance,
      minorsAttendance: this.minorsAttendance
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyFilter(): void {
    const searchTerm = (this.searchControl.value || '').toLowerCase();
    this.filteredMembers = this.members
      .filter(m => {
        const fullName = `${m.firstName} ${m.lastName}`.toLowerCase();
        return !searchTerm || fullName.includes(searchTerm);
      })
      .filter(m => this.attendanceFilters['minor'].includes(this.isMinor(m) ? 'minor' : 'adult'))
      .filter(m => this.attendanceFilters['sealed'].includes(m.isSealed ? 'sealed' : 'unsealed'))
      .filter(m => this.attendanceFilters['visitor'].includes(this.isVisitor(m) ? 'visitor' : 'member'))
      .filter(m => this.attendanceFilters['attended'].includes(m.attended ? 'present' : 'absent'))
      .sort((a, b) => this.compareMembers(a, b));
    const totalPages = Math.max(1, Math.ceil(this.filteredMembers.length / this.pageSize));
    this.currentPage = Math.min(this.currentPage, totalPages);
    this.cdr.markForCheck();
  }

  private showRosterInformationOnFirstVisit(): void {
    try {
      if (localStorage.getItem(AttendanceComponent.ROSTER_INFORMATION_KEY)) {
        return;
      }

      localStorage.setItem(AttendanceComponent.ROSTER_INFORMATION_KEY, 'true');
    } catch {
      // La notificación sigue disponible desde el icono si el almacenamiento está bloqueado.
    }

    this.showRosterInformation();
  }

  onDesktopSearch(term: string): void {
    this.currentPage = 1;
    this.searchControl.setValue(term);
  }

  onFilterRemove(key: string): void {
    this.desktopFilterForm.get(key)?.setValue(this.getFilterOptions(key).map(option => option.value));
  }

  onSortChange(sort: ToolbarSortSelection): void {
    this.currentPage = 1;
    this.sortState = sort;
    this.dataTable?.setSort(sort);
    this.applyFilter();
  }

  onDensityChange(density: 'compact' | 'normal' | 'comfortable'): void {
    this.tableDensity = density;
    this.dataTable?.setDensity(density);
  }

  onColumnVisibilityChange(change: { key: string; visible: boolean }): void {
    this.dataTable?.setColumnVisibility(change.key, change.visible);
  }

  onPageChange(event: { page: number }): void {
    this.currentPage = event.page;
    this.cdr.markForCheck();
  }

  onPageSizeChange(event: { pageSize: number }): void {
    this.pageSize = event.pageSize;
    this.currentPage = 1;
    this.cdr.markForCheck();
  }

  isVisitor(member: MemberAttendanceDto): boolean {
    return member.isVisitor === true || member.attendanceCategory === 'Visitor';
  }

  private compareMembers(a: MemberAttendanceDto, b: MemberAttendanceDto): number {
    const direction = this.sortState.direction === 'asc' ? 1 : -1;
    const values: Record<string, [string | boolean, string | boolean]> = {
      name: [this.getFullName(a), this.getFullName(b)],
      age: [this.isMinor(a), this.isMinor(b)],
      sealed: [a.isSealed, b.isSealed],
      visitor: [this.isVisitor(a), this.isVisitor(b)],
      attended: [a.attended, b.attended]
    };
    const [left, right] = values[this.sortState.sortBy] ?? values['name'];
    return String(left).localeCompare(String(right), 'es', { sensitivity: 'base' }) * direction;
  }

  private refreshFilters(): void {
    this.currentPage = 1;
    this.activeFilters = Object.entries(this.attendanceFilters)
      .filter(([key, values]) => values.length !== this.getFilterOptions(key).length)
      .map(([key, values]) => ({
        key,
        label: values.length === 0
          ? `${this.getFilterLabel(key)}: Ninguno`
          : `${this.getFilterLabel(key)}: ${values.map(value => this.getFilterOptions(key).find(option => option.value === value)?.label ?? value).join(', ')}`,
        removable: true,
      }));
    this.applyFilter();
  }

  private getFilterOptions(key: string): readonly DataTableMultiSelectFilterOption[] {
    const options: Record<string, readonly DataTableMultiSelectFilterOption[]> = {
      minor: this.minorFilterOptions,
      sealed: this.sealedFilterOptions,
      visitor: this.visitorFilterOptions,
      attended: this.attendedFilterOptions,
    };
    return options[key] ?? [];
  }

  private getFilterLabel(key: string): string {
    const labels: Record<string, string> = {
      minor: 'Edad',
      sealed: 'Sellado',
      visitor: 'Visita',
      attended: 'Asistencia',
    };
    return labels[key] ?? key;
  }

  isMemberAttending(memberId: string): boolean {
    const member = this.members.find(m => m.personId === memberId);
    return member ? member.attended : false;
  }

  isToggling(memberId: string): boolean {
    return this.toggling.has(memberId);
  }

  onAttendanceChange(member: MemberAttendanceDto, isChecked: boolean): void {
    if (!this.divineServiceId) {
      this.notificationService.error('ID del servicio no disponible');
      return;
    }
    if (this.readonly) {
      return;
    }

    // Marcar como en proceso
    this.toggling.add(member.personId);
    this.cdr.markForCheck();

    const dto: CreateOrUpdateAttendanceDto = {
      divineServiceId: this.divineServiceId,
      attendantId: member.personId,
      notes: ''
    };

    if (isChecked) {
      // Marcar asistencia
      this.divineServiceService.markAttendance(this.divineServiceId, member.personId, dto)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.updateMemberAttendance(member.personId, true);
            this.toggling.delete(member.personId);
            this.cdr.markForCheck();
          },
          error: (error: any) => {
            console.error('Error marking attendance:', error);
            this.notificationService.error('Error al marcar asistencia');
            this.toggling.delete(member.personId);
            this.cdr.markForCheck();
          }
        });
    } else {
      // Desmarcar asistencia
      this.divineServiceService.unmarkAttendance(this.divineServiceId, member.personId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.updateMemberAttendance(member.personId, false);
            this.toggling.delete(member.personId);
            this.cdr.markForCheck();
          },
          error: (error: any) => {
            console.error('Error unmarking attendance:', error);
            this.notificationService.error('Error al desmarcar asistencia');
            this.toggling.delete(member.personId);
            this.cdr.markForCheck();
          }
        });
    }
  }

  onMarkAll(): void {
    if (!this.divineServiceId) {
      this.notificationService.error('ID del servicio no disponible');
      return;
    }
    if (this.readonly) {
      return;
    }

    // Emitir solo los IDs de los miembros NO asistentes del filtro actual
    const unmarkedFilteredIds = this.filteredMembers
      .filter(m => !m.attended)
      .map(m => m.personId);

    if (unmarkedFilteredIds.length === 0) {
      return;
    }

    this.divineServiceService.bulkMarkAttendance(this.divineServiceId, unmarkedFilteredIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Actualizar todos los miembros
          this.members = this.members.map(member => {
            if (unmarkedFilteredIds.includes(member.personId)) {
              return { ...member, attended: true };
            }
            return member;
          });
          this.applyFilter();
          this.membersUpdated.emit(this.members);
          this.emitTotals();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error bulk marking attendance:', error);
          this.notificationService.error('Error al marcar asistencia para todos');
          this.cdr.markForCheck();
        }
      });
  }

  onUnmarkAll(): void {
    if (!this.divineServiceId) {
      this.notificationService.error('ID del servicio no disponible');
      return;
    }
    if (this.readonly) {
      return;
    }

    // Emitir solo los IDs de los miembros asistentes del filtro actual
    const markedFilteredIds = this.filteredMembers
      .filter(m => m.attended)
      .map(m => m.personId);

    if (markedFilteredIds.length === 0) {
      return;
    }

    this.divineServiceService.bulkUnmarkAttendance(this.divineServiceId, markedFilteredIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Actualizar todos los miembros
          this.members = this.members.map(member => {
            if (markedFilteredIds.includes(member.personId)) {
              return { ...member, attended: false };
            }
            return member;
          });
          this.applyFilter();
          this.membersUpdated.emit(this.members);
          this.emitTotals();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error bulk unmarking attendance:', error);
          this.notificationService.error('Error al desmarcar asistencia para todos');
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Actualiza un miembro individual en la lista y emite los cambios
   */
  private updateMemberAttendance(memberId: string, attended: boolean): void {
    this.members = this.members.map(m =>
      m.personId === memberId ? { ...m, attended } : m
    );
    this.applyFilter();
    this.membersUpdated.emit(this.members);
    this.emitTotals();
  }

  /**
   * Emite los totales actuales de asistencia
   */
  private emitTotals(): void {
    this.attendanceTotalsChanged.emit({
      adultsAttendance: this.adultsAttendance,
      minorsAttendance: this.minorsAttendance
    });
  }

  getFullName(member: MemberAttendanceDto): string {
    return `${member.firstName} ${member.lastName}`;
  }

  get attendanceCount(): number {
    return this.filteredMembers.filter(m => m.attended).length;
  }

  get totalMembers(): number {
    return this.filteredMembers.length;
  }

  get minorAttendanceCount(): number {
    return MemberFilterHelper.filterMinors(this.filteredMembers, true).length;
  }

  get adultAttendanceCount(): number {
    return MemberFilterHelper.filterAdults(this.filteredMembers, true).length;
  }

  get totalAttendanceCount(): number {
    return this.minorAttendanceCount + this.adultAttendanceCount;
  }

  get attendancePercentage(): number {
    return this.totalMembers > 0 ? (this.totalAttendanceCount / this.totalMembers) * 100 : 0;
  }
}
